/**
 * COLA DURABLE DE AUDIO Y EVIDENCIA — «un audio grabado no se pierde nunca».
 *
 * El problema real (jornada CCJ del 6-ago-2026): la evidencia de media se
 * subía a pulso y su único respaldo era estado de React. Si el POST fallaba
 * —y falló: Apache cortaba las subidas lentas con 408— el audio existía solo
 * en la memoria de esa pestaña. Recargar la página, cambiar de app o que el
 * navegador matara la pestaña por memoria borraba la grabación para siempre.
 * La cola de aportes de texto (`colaOffline`) sí sobrevivía, porque cabe en
 * localStorage; un Blob de audio no.
 *
 * Diseño — ESCRITURA ADELANTADA (write-ahead):
 *   1. Al terminar de grabar, el blob se guarda en IndexedDB ANTES de intentar
 *      subirlo. Desde ese instante la grabación está a salvo aunque el
 *      dispositivo se apague en el siguiente milisegundo.
 *   2. Recién entonces se intenta el envío. Sale de la cola SOLO cuando el
 *      servidor confirma (201, o 200 por deduplicación).
 *   3. Reintento seguro por contrato: cada ítem lleva su `idem` y el backend
 *      deduplica por `payload.idem`, así que reintentar jamás duplica (C1).
 *
 * Qué NUNCA hace: borrar un ítem que no se entregó. Ni siquiera ante un error
 * definitivo del servidor (actividad cerrada, archivo rechazado). Esos quedan
 * marcados `bloqueado` con su motivo y se pueden DESCARGAR al teléfono. Perder
 * la voz de alguien porque el servidor dijo que no es exactamente lo que no
 * puede volver a pasar.
 *
 * Orden de envío: por el instante en que ARRANCÓ cada grabación, no por el de
 * encolado. Si tres notas esperan, entran al acta en el orden en que se
 * dijeron.
 */
import { create } from 'zustand';
import { ApiError } from './api';
import { subirMedia, type MediaTipo } from './media';
import {
  ALMACEN_PENDIENTES,
  conAlmacen as conAlmacenDb,
} from './db';
import {
  borrarGrabacion,
  duracionAproximada,
  listarHuerfanas,
  reensamblar,
} from './grabacionEnCurso';

/** Una grabación (o adjunto) esperando entrar al acta. */
export interface MediaEnCola {
  /** uuid del cliente; el backend deduplica por él (reintento seguro, C1). */
  idem: string;
  actividadId: string;
  tipo: MediaTipo;
  blob: Blob;
  filename: string;
  mime: string;
  /** ISO-8601 del arranque de la grabación: ordena la cola y sella el acta. */
  inicio: string;
  /** ISO-8601 del final de la grabación. */
  fin: string;
  /** Duración aproximada en segundos (para mostrarla sin abrir el blob). */
  duracionS: number;
  /** Payload estructurado del manifiesto (voz dentro del formato, ADR-0005 §5). */
  campos?: Record<string, unknown>;
  parentId?: string;
  microfonoSala?: boolean;
  numHablantes?: number;
  /** Reintentos consumidos (diagnóstico). */
  intentos: number;
  /**
   * Se reensambló desde los trozos de una grabación interrumpida (la pestaña
   * murió o la máquina se suspendió mientras grababa). La UI lo dice: quien
   * grabó merece saber que su audio se rescató.
   */
  recuperada?: boolean;
  /**
   * El servidor lo rechazó de forma definitiva (4xx que no es 408). NO se
   * borra: se marca, se explica y se ofrece descargarlo.
   */
  bloqueado?: string;
}

/* ============================ Persistencia ============================= */

const conAlmacen = <T,>(
  modo: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => conAlmacenDb(ALMACEN_PENDIENTES, modo, fn);

/** Todo lo pendiente, en el orden en que se dijo (por inicio de grabación). */
export async function listarPendientes(): Promise<MediaEnCola[]> {
  const todos = await conAlmacen<MediaEnCola[]>('readonly', (s) => s.getAll());
  return todos.sort((a, b) => (a.inicio < b.inicio ? -1 : a.inicio > b.inicio ? 1 : 0));
}

async function guardar(item: MediaEnCola): Promise<void> {
  await conAlmacen('readwrite', (s) => s.put(item) as IDBRequest<IDBValidKey>);
}

async function borrar(idem: string): Promise<void> {
  await conAlmacen('readwrite', (s) => s.delete(idem) as unknown as IDBRequest<undefined>);
}

/* ============================ Store reactivo =========================== */

interface ColaMediaState {
  items: MediaEnCola[];
  vaciando: boolean;
  /** Relee IndexedDB (al arrancar la app y tras cada cambio). */
  refrescar: () => Promise<void>;
  /** Guarda la grabación y la intenta enviar. Devuelve true si ya entró. */
  encolarYEnviar: (item: Omit<MediaEnCola, 'intentos'>) => Promise<boolean>;
  /** Reintenta todos los pendientes, en orden de grabación. */
  flush: () => Promise<void>;
  /** Saca un ítem tras entregarlo (o cuando la persona decide descartarlo). */
  descartar: (idem: string) => Promise<void>;
  /** Desmarca un bloqueado y lo reenvía (p. ej. tras corregir el plan). */
  reintentar: (idem: string) => Promise<void>;
}

export const useColaMedia = create<ColaMediaState>((set, get) => ({
  items: [],
  vaciando: false,

  refrescar: async () => {
    try {
      set({ items: await listarPendientes() });
    } catch {
      /* sin IndexedDB no hay cola persistente; la subida directa sigue viva */
    }
  },

  encolarYEnviar: async (base) => {
    const item: MediaEnCola = { ...base, intentos: 0 };
    // ESCRITURA ADELANTADA: a salvo antes de tocar la red.
    try {
      await guardar(item);
      await get().refrescar();
    } catch {
      // Sin almacén persistente igual intentamos: mejor eso que nada.
    }
    const entregado = await intentarEnvio(item);
    if (entregado) {
      await get().descartar(item.idem);
      return true;
    }
    await get().refrescar();
    return false;
  },

  flush: async () => {
    if (get().vaciando) return;
    const pendientes = await listarPendientes();
    if (pendientes.length === 0) return;
    set({ vaciando: true });
    try {
      for (const item of pendientes) {
        if (item.bloqueado) continue; // ya sabemos que el servidor no lo acepta
        const entregado = await intentarEnvio(item);
        if (entregado) {
          await borrar(item.idem);
        } else if (!navigator.onLine) {
          break; // sin red: no gastes los demás intentos
        }
        await get().refrescar();
      }
    } finally {
      set({ vaciando: false });
      await get().refrescar();
    }
  },

  descartar: async (idem) => {
    await borrar(idem);
    await get().refrescar();
  },

  reintentar: async (idem) => {
    // El bloqueo fue definitivo por una causa que ya se corrigió del lado del
    // servidor (p. ej. el plan que habilita el micrófono de sala). Limpiamos la
    // marca y reintentamos ese ítem: si entra, se borra; si vuelve a fallar,
    // se re-bloquea con el detalle nuevo.
    const pendientes = await listarPendientes();
    const item = pendientes.find((i) => i.idem === idem);
    if (!item) return;
    const limpio: MediaEnCola = { ...item, bloqueado: undefined };
    try {
      await guardar(limpio);
    } catch {
      /* sin almacén igual intentamos abajo */
    }
    await get().refrescar();
    const entregado = await intentarEnvio(limpio);
    if (entregado) await borrar(idem);
    await get().refrescar();
  },
}));

/**
 * Un intento de entrega. `true` = el servidor lo confirmó.
 *
 * Reintentable: fallo de red (0), 408 (Apache cortó la subida lenta — el caso
 * exacto del 6-ago) y 5xx. Definitivo: el resto de 4xx, que se MARCA sin
 * borrar el audio.
 */
async function intentarEnvio(item: MediaEnCola): Promise<boolean> {
  try {
    await subirMedia(
      item.actividadId,
      item.blob,
      item.tipo,
      { filename: item.filename, mime: item.mime },
      item.idem,
      {
        campos: item.campos,
        parentId: item.parentId,
        microfonoSala: item.microfonoSala,
        inicioGrabacion: item.inicio,
        finGrabacion: item.fin,
        numHablantes: item.numHablantes,
      },
    );
    return true;
  } catch (err) {
    const status = err instanceof ApiError ? err.status : 0;
    const reintentable = status === 0 || status === 408 || status >= 500;
    const actualizado: MediaEnCola = {
      ...item,
      intentos: item.intentos + 1,
      bloqueado: reintentable
        ? undefined
        : err instanceof ApiError
          ? err.detail
          : 'El servidor no aceptó esta grabación.',
    };
    try {
      await guardar(actualizado);
    } catch {
      /* nada más que hacer */
    }
    return false;
  }
}

/* ============================ Recuperación ============================= */

/**
 * Rescata las grabaciones que nunca se cerraron: la pestaña murió, la laptop
 * se suspendió o se cortó la luz MIENTRAS se grababa. Antes se perdían enteras
 * —el blob solo existía en memoria—; ahora sus trozos están en IndexedDB y se
 * reensamblan acá.
 *
 * Se recuperan sin preguntar, y es deliberado: solo hay huérfana si llegó a
 * escribirse al menos un trozo, es decir si se grabó de verdad (≥ un
 * `timeslice`). Un roce del botón no deja nada. Y quien cancela a propósito
 * borra sus trozos en el acto, así que una huérfana es siempre audio real que
 * alguien dijo.
 *
 * El `idem` de la cola ES el id de la grabación: si el envío original sí había
 * llegado antes de morir la pestaña, el backend deduplica y no duplica el
 * aporte (C1).
 */
export async function recuperarGrabacionesInterrumpidas(): Promise<number> {
  let recuperadas = 0;
  try {
    const huerfanas = await listarHuerfanas();
    for (const g of huerfanas) {
      const blob = await reensamblar(g.id);
      if (!blob || blob.size === 0) {
        await borrarGrabacion(g.id);
        continue;
      }
      await useColaMedia.getState().encolarYEnviar({
        idem: g.id,
        actividadId: g.actividadId,
        tipo: 'audio',
        blob,
        filename: `${g.contexto === 'sala' ? 'sala' : 'nota-de-voz'}.${g.ext}`,
        mime: g.mime,
        inicio: g.inicio,
        fin: g.ultimoTrozo,
        duracionS: duracionAproximada(g),
        campos: g.campos,
        microfonoSala: g.microfonoSala,
        numHablantes: g.numHablantes,
        recuperada: true,
      });
      // Ya está a salvo en la cola de envío: los trozos pueden irse.
      await borrarGrabacion(g.id);
      recuperadas += 1;
    }
  } catch {
    /* sin IndexedDB no hay nada que recuperar */
  }
  return recuperadas;
}

/* ============================ Disparadores ============================= */

if (typeof window !== 'undefined') {
  // Al arrancar la app, en orden: primero rescatar lo que quedó a medio grabar
  // (pestaña muerta, laptop suspendida), después reintentar toda la cola.
  void recuperarGrabacionesInterrumpidas()
    .then(() => useColaMedia.getState().refrescar())
    .then(() => {
      if (useColaMedia.getState().items.length > 0) void useColaMedia.getState().flush();
    });

  window.addEventListener('online', () => {
    void useColaMedia.getState().flush();
  });

  // Volver al frente es el momento típico en que la red vuelve a servir.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void useColaMedia.getState().flush();
  });

  /**
   * Cerrar o recargar con audio sin entregar: el navegador pregunta.
   *
   * Es una RED DE SEGURIDAD, no la garantía: la garantía es IndexedDB, que
   * sobrevive la recarga y reintenta sola al volver. Por eso avisamos en vez
   * de bloquear —un navegador no puede impedir que cierren la pestaña, y
   * fingir que sí sería peor que decir la verdad—.
   */
  window.addEventListener('beforeunload', (e) => {
    const pendientes = useColaMedia
      .getState()
      .items.filter((i) => !i.bloqueado).length;
    if (pendientes > 0) {
      e.preventDefault();
      e.returnValue = '';
    }
  });
}
